import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import type { Employee } from '../types';
import {
  Zap, User, Users, ArrowRight, HardHat, Briefcase, FileText, GraduationCap, Shield, ChevronLeft, Lock, KeyRound
} from 'lucide-react';

// Rôles nécessitant un mot de passe renforcé (au lieu d'un simple PIN)
const STRONG_PASSWORD_ROLES = ['admin', 'bureau_etude', 'secretariat'];
const requiresStrongPassword = (role: string): boolean => STRONG_PASSWORD_ROLES.includes(role);

export default function Login() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      // Utiliser la fonction RPC sécurisée (ne renvoie pas pin_hash)
      const { data, error } = await supabase.rpc('get_login_list');
      if (error) {
        console.error('Erreur chargement employés:', error);
        return;
      }
      if (data) {
        // Trier par full_name côté client (la fonction RPC ne trie pas)
        // Cast via unknown car la RPC renvoie un type partiel (sans pin_hash)
        const sorted = [...data].sort((a, b) => a.full_name.localeCompare(b.full_name, 'fr'));
        setEmployees(sorted as unknown as Employee[]);
      }
    };
    fetchEmployees();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmp) return;
    setLoading(true);
    setError('');

    // Utiliser Supabase Auth avec l'email généré
    const email = `${selectedEmp.id}@centralis.local`;
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password: pin
    });

    if (error) {
      console.error('Erreur connexion:', error);
      setError(requiresStrongPassword(selectedEmp.role) ? 'Mot de passe incorrect' : 'Code PIN incorrect');
      setPin('');
      setLoading(false);
      return;
    }

    if (data.user) {
      // La session est maintenant persistée par Supabase Auth
      // AuthContext va automatiquement récupérer les données employé via onAuthStateChange
      navigate('/');
    } else {
      setError('Erreur de connexion. Veuillez réessayer.');
      setPin('');
      setLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch(role) {
      case 'admin': return <Shield size={18} className="text-indigo-600" />;
      case 'chef_chantier': return <HardHat size={18} className="text-amber-600" />;
      case 'chef_equipe': return <Users size={18} className="text-teal-600" />;
      case 'electricien': return <Zap size={18} className="text-emerald-600" />;
      case 'bureau_etude': return <Briefcase size={18} className="text-sky-600" />;
      case 'apprenti_be': return <GraduationCap size={18} className="text-sky-600" />;
      case 'secretariat': return <FileText size={18} className="text-purple-600" />;
      default: return <User size={18} className="text-slate-500" />;
    }
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Direction',
      chef_chantier: 'Chef de Chantier',
      chef_equipe: 'Chef d\'Équipe',
      electricien: 'Électricien',
      bureau_etude: 'Bureau d\'Étude',
      apprenti_be: 'Apprenti'
    };
    return labels[role] || 'Collaborateur';
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8fafc',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '"Inter", sans-serif'
    }}>
      
      <div style={{ 
        background: 'white', 
        width: '100%', 
        maxWidth: '400px', 
        borderRadius: '24px', 
        boxShadow: '0 10px 40px -10px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}>
        
        {/* HEADER BRANDING */}
        <div style={{ 
          padding: '40px 30px 20px', 
          textAlign: 'center', 
          background: 'white',
          borderBottom: '1px solid #f1f5f9'
        }}>
          {/* Logo Centralis */}
          <div style={{ marginBottom: '25px' }}>
            <img
              src="/logo.svg"
              alt="Centralis"
              style={{
                height: '46px',
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
            <span style={{
              fontFamily: '"Product Sans", "Outfit", sans-serif',
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '5px',
              display: 'block',
              marginTop: '5px'
            }}>
              pour
            </span>
          </div>

          {/* Logo Oger Energies */}
          <div style={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70px'
          }}>
            <img 
              src="/logo.png" 
              alt="Oger Energies" 
              style={{ 
                height: '60px', 
                maxWidth: '100%', 
                objectFit: 'contain'
              }} 
            />
          </div>
        </div>

        <div style={{ padding: '30px' }}>
          
          {!selectedEmp ? (
            <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '380px', overflowY: 'auto' }}>
                {employees.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => setSelectedEmp(emp)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '14px 16px', 
                      background: 'white', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '12px',
                      cursor: 'pointer', 
                      transition: 'all 0.2s',
                      textAlign: 'left'
                    }}
                    onMouseEnter={e => { 
                      e.currentTarget.style.borderColor = '#cbd5e1'; 
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => { 
                      e.currentTarget.style.borderColor = '#e2e8f0'; 
                      e.currentTarget.style.transform = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ 
                        color: '#64748b',
                        background: '#f8fafc',
                        padding: '6px',
                        borderRadius: '8px'
                      }}>
                        {getRoleIcon(emp.role)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.95rem' }}>{emp.full_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{getRoleLabel(emp.role)}</div>
                      </div>
                    </div>
                    <ArrowRight size={16} color="#cbd5e1" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={handleLogin} style={{ animation: 'slideInRight 0.3s ease-out' }}>
              {(() => {
                const isStrongPwd = requiresStrongPassword(selectedEmp.role);
                return (
                  <>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                      <div style={{
                        width: '64px', height: '64px',
                        background: isStrongPwd ? '#f5f3ff' : '#fff7f0',
                        borderRadius: '50%',
                        margin: '0 auto 16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isStrongPwd ? '#7c3aed' : '#fb803c', fontSize: '1.5rem', fontWeight: 'bold'
                      }}>
                        {isStrongPwd ? <Lock size={28} /> : selectedEmp.full_name.charAt(0)}
                      </div>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
                        {selectedEmp.full_name.split(' ').slice(1).join(' ') || selectedEmp.full_name}
                      </h3>
                      <p style={{ color: '#64748b', fontSize: '0.9rem', marginTop: '4px' }}>
                        {isStrongPwd ? 'Saisissez votre mot de passe' : 'Saisissez votre code PIN'}
                      </p>
                      {isStrongPwd && (
                        <div style={{
                          marginTop: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: '#f3e8ff',
                          color: '#7c3aed',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '0.7rem',
                          fontWeight: '600'
                        }}>
                          <KeyRound size={12} /> Accès sécurisé
                        </div>
                      )}
                    </div>

                    <div style={{ position: 'relative', marginBottom: '24px' }}>
                      <input
                        type="password"
                        value={pin}
                        onChange={(e) => {
                          if (isStrongPwd) {
                            setPin(e.target.value);
                          } else {
                            // PIN : uniquement chiffres, max 6
                            setPin(e.target.value.replace(/\D/g, '').slice(0, 6));
                          }
                          setError('');
                        }}
                        placeholder={isStrongPwd ? 'Mot de passe' : '• • • • • •'}
                        autoFocus
                        inputMode={isStrongPwd ? 'text' : 'numeric'}
                        maxLength={isStrongPwd ? 50 : 6}
                        style={{
                          width: '100%', padding: '16px',
                          fontSize: isStrongPwd ? '1rem' : '2rem',
                          letterSpacing: isStrongPwd ? 'normal' : '0.5em',
                          textAlign: isStrongPwd ? 'left' : 'center',
                          border: error ? '2px solid #ef4444' : `2px solid ${isStrongPwd ? '#ddd6fe' : '#e2e8f0'}`,
                          borderRadius: '16px',
                          background: isStrongPwd ? '#faf5ff' : '#fff',
                          outline: 'none',
                          transition: 'border-color 0.2s',
                          color: '#1e293b',
                          fontWeight: isStrongPwd ? '500' : '700',
                          fontFamily: isStrongPwd ? 'inherit' : 'monospace'
                        }}
                        onFocus={e => !error && (e.target.style.borderColor = isStrongPwd ? '#7c3aed' : '#fb803c')}
                        onBlur={e => !error && (e.target.style.borderColor = isStrongPwd ? '#ddd6fe' : '#e2e8f0')}
                      />
                    </div>
                  </>
                );
              })()}
              
              {error && (
                <div style={{ 
                  background: '#fef2f2', color: '#ef4444', 
                  padding: '10px', borderRadius: '8px', 
                  fontSize: '0.85rem', textAlign: 'center', marginBottom: '20px', 
                  fontWeight: '600'
                }}>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                disabled={loading}
                style={{ 
                  width: '100%', padding: '16px',
                  background: '#fb803c',
                  color: 'white', border: 'none', borderRadius: '14px',
                  fontSize: '1rem', fontWeight: '600', cursor: loading ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: '0 4px 6px rgba(251, 128, 60, 0.25)'
                }}
              >
                {loading ? 'Connexion...' : 'Valider'}
              </button>
              
              <button 
                type="button" 
                onClick={() => { setSelectedEmp(null); setPin(''); setError(''); }}
                style={{ 
                  width: '100%', marginTop: '16px', padding: '8px', background: 'transparent', border: 'none', 
                  color: '#64748b', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  fontWeight: '500'
                }}
              >
                <ChevronLeft size={16} /> Retour
              </button>
            </form>
          )}
        </div>
      </div>

      {/* FOOTER FIXE EN BAS */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0',
        zIndex: 50
      }}>
        <div style={{
          width: '100%',
          height: '3px',
          background: 'linear-gradient(90deg, #f1b359, #fb803c, #f19c59, #fb803c, #f1b359)'
        }} />
        <div style={{
          width: '100%',
          padding: '10px 0',
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          textAlign: 'center'
        }}>
          <span style={{
            fontSize: '0.72rem',
            color: '#0b0802',
            opacity: 0.45,
            letterSpacing: '0.3px'
          }}>
            © 2026 Centralis — Tous droits réservés
          </span>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}